import {ThreadPrimitive, useAui} from '@assistant-ui/react';
import {Button, CalloutIcon, DotProgress, Icon} from '@wandb/components';
import React, {type FC, useCallback, useRef, useState} from 'react';

import {useActiveThreadLoadState} from '../../agentChat/store/context';
import {ErrorBoundary} from '../ErrorBoundary';
import {WBAgentComposer} from './WBAgentComposer';
import {AssistantMessage} from './WBAgentMessageAssistant';
import {UserMessage} from './WBAgentMessageUser';

const messageComponents = {UserMessage, AssistantMessage};

export const WBAgentThread = () => {
  const {
    activeThreadId,
    isLoading,
    error: loadError,
  } = useActiveThreadLoadState();

  const aui = useAui();

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <DotProgress aria-label="Loading thread" />
      </div>
    );
  }

  if (loadError && activeThreadId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-12 px-16">
        <CalloutIcon icon="warning" color="red" size="md" />
        <span className="font-semibold text-base">
          Failed to load conversation
        </span>
        <Button
          variant="secondary"
          startIcon="reload-refresh"
          onPress={() => aui.threads().switchToThread(activeThreadId)}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <ThreadPrimitive.Root
      className="flex flex-1 min-h-0 flex-col"
      style={{
        ['--thread-max-width' as string]: '44rem',
      }}>
      <ThreadPrimitive.ViewportProvider>
        <ThreadPrimitive.Viewport
          turnAnchor="top"
          autoScroll
          className="flex flex-1 flex-col overflow-y-auto scroll-smooth overscroll-contain px-24 pt-12 [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:#0d0f1252_transparent]">
          <MessageRecoveryBoundary>
            <ThreadPrimitive.Empty>
              <div className="flex flex-1 justify-center items-center gap-8">
                <Icon
                  aria-label="workspace-agent-logo"
                  icon="lightboard-solid"
                  color="teal"
                />
                <span className="text-center text-[20px] font-semibold">
                  {' '}
                  How can I help you today?
                </span>
              </div>
            </ThreadPrimitive.Empty>
            <div className="flex flex-col shrink-0">
              <ThreadPrimitive.Messages components={messageComponents} />
            </div>
          </MessageRecoveryBoundary>
        </ThreadPrimitive.Viewport>
        <div className="px-24 py-16">
          <WBAgentComposer />
        </div>
      </ThreadPrimitive.ViewportProvider>
    </ThreadPrimitive.Root>
  );
};

/**
 * Auto-recovers from AUI tearing crashes during thread switches [1] by
 * remounting children with fresh subscriptions. Allows one recovery per thread
 * switch; a second error re-throws from `renderError` (error boundaries cannot
 * catch their own render errors) so it bubbles to `AgentChatProvider`.
 *
 * [1] https://github.com/assistant-ui/assistant-ui/issues/3652
 */
const MessageRecoveryBoundary: FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const {activeThreadId} = useActiveThreadLoadState();
  const [recoveryKey, setRecoveryKey] = useState(0);
  const recovered = useRef(false);

  // Reset on thread switch — uses the store value (tick 1), which updates
  // before AUI processes the committed switch (tick 2).
  const prevThreadId = useRef(activeThreadId);
  if (prevThreadId.current !== activeThreadId) {
    prevThreadId.current = activeThreadId;
    recovered.current = false;
  }

  const handleError = useCallback(() => {
    if (recovered.current) {
      return;
    }

    recovered.current = true;

    // Wait one frame so AUI's microtask scheduler finishes flushing before
    // remounting children with new subscriptions.
    requestAnimationFrame(() => setRecoveryKey(k => k + 1));
  }, []);

  const renderError = useCallback((error: Error) => {
    if (recovered.current) {
      throw error; // Propagate a second error up to the next boundary.
    }

    return <></>;
  }, []);

  return (
    <ErrorBoundary
      key={recoveryKey}
      silent
      onError={handleError}
      renderError={renderError}>
      {children}
    </ErrorBoundary>
  );
};
